import { Injectable } from '@angular/core';
import { EventType } from 'matrix-js-sdk';
import { RoomPowerLevel } from '../../model/room-power-level';
import { FormControl } from '@angular/forms';

export type PowerLevelAction = 'kick' | 'invite';

export type PowerLevelEventType =
  | EventType.RoomName
  | EventType.RoomTopic
  | EventType.RoomAvatar
  | EventType.RoomHistoryVisibility
  | EventType.RoomPinnedEvents;

/**
 * Serviceklasse zur Abfrage der PowerLevels der User zu Aktionen oder defaults in Räumen.
 * Begründung: keine direkte AFO erfüllt
 */
@Injectable({
  providedIn: 'root'
})
export class RoomPowerLevelService {
  canDoEvent(
    eventType: PowerLevelEventType,
    powerLevel: RoomPowerLevel | undefined,
    userId: string | undefined
  ): boolean {
    if (!powerLevel) {
      throw new Error('No RoomPowerLevel, when calling canDoEvent');
    }
    if (!userId) {
      throw new Error('No userId, when calling canDoEvent');
    }
    const userPowerLevel = this.getUserPowerLevel(userId, powerLevel);
    const eventPowerLevel = this.requiredPowerLevelForEvents(eventType, powerLevel);
    return userPowerLevel >= eventPowerLevel;
  }

  canDoAction(action: PowerLevelAction, powerLevel: RoomPowerLevel | undefined, userId: string | undefined): boolean {
    if (!powerLevel) {
      throw new Error('No RoomPowerLevel, when calling canDoAction');
    }
    if (!userId) {
      throw new Error('No userId, when calling canDoAction');
    }
    const userPowerLevel = this.getUserPowerLevel(userId, powerLevel);
    const actionPowerLevel = this.requiredPowerLevelForAction(action, powerLevel);
    return userPowerLevel >= actionPowerLevel;
  }

  applyPowerLevelToFormControl(
    control: FormControl,
    canDo: boolean,
    opts?: {
      forceDisable: boolean;
      inverse: boolean;
    }
  ): void {
    const { forceDisable = false, inverse = false } = opts ?? {};
    const finalCanDo = inverse ? !canDo : canDo;
    if (forceDisable || !finalCanDo) {
      if (control.enabled) {
        control.disable({ emitEvent: false });
      }
    } else if (control.disabled) {
      control.enable({ emitEvent: false });
    }
  }

  private getUserPowerLevel(userId: string, powerLevel: RoomPowerLevel): number {
    return powerLevel.users[userId] ?? powerLevel.users_default ?? 0;
  }

  private requiredPowerLevelForAction(action: PowerLevelAction, powerLevel: RoomPowerLevel): number {
    switch (action) {
      case 'kick':
        return powerLevel.kick;
      case 'invite':
        return powerLevel.invite;
    }
  }

  private requiredPowerLevelForEvents(eventType: PowerLevelEventType, powerLevel: RoomPowerLevel): number {
    if (eventType in powerLevel.events) {
      // Durch die Überprüfung mit 'in' sollte der Return nicht undefined sein.
      return powerLevel.events[eventType]!;
    }
    return powerLevel.state_default;
  }
}
